/**
 * ActivityTimeline Component
 *
 * Displays a feed of recent activity from getMyDashboard.recentActivity.
 * Shows activity type, description, and timestamp in a vertical timeline.
 *
 * Requirements: 7.1, 7.2 - Recent activity timeline on dashboard
 */

import { Card, CardContent, CardHeader, CardTitle } from '@bloomcode/ui';
import {
  Mail,
  Phone,
  MessageSquare,
  FileText,
  Bell,
  Calendar,
  CheckCircle,
  Activity,
} from '@bloomcode/icons';

export interface ActivityItem {
  id: string;
  type: string;
  description: string;
  date: string;
  metadata?: Record<string, unknown> | null;
}

interface ActivityTimelineProps {
  activities: ActivityItem[];
  isLoading?: boolean;
}

function ActivityTimelineSkeleton() {
  return (
    <Card>
      <CardHeader>
        <div className="h-5 w-36 animate-pulse rounded bg-muted" />
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex gap-3">
              <div className="h-8 w-8 animate-pulse rounded-full bg-muted" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
                <div className="h-3 w-1/3 animate-pulse rounded bg-muted" />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

const activityTypeConfig: Record<
  string,
  {
    icon: React.ComponentType<{ className?: string }>;
    colorClass: string;
    label: string;
  }
> = {
  EMAIL: {
    icon: Mail,
    colorClass: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    label: 'Email',
  },
  CALL: {
    icon: Phone,
    colorClass: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    label: 'Call',
  },
  MEETING: {
    icon: Calendar,
    colorClass: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
    label: 'Meeting',
  },
  NOTE: {
    icon: FileText,
    colorClass: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    label: 'Note',
  },
  TASK: {
    icon: CheckCircle,
    colorClass: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400',
    label: 'Task',
  },
  MESSAGE: {
    icon: MessageSquare,
    colorClass: 'bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
    label: 'Message',
  },
  NOTIFICATION: {
    icon: Bell,
    colorClass: 'bg-rose-500/10 text-rose-600 dark:text-rose-400',
    label: 'Notification',
  },
};

const defaultConfig = {
  icon: Activity,
  colorClass: 'bg-muted text-muted-foreground',
  label: 'Activity',
};

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface TimelineItemProps {
  activity: ActivityItem;
  isLast: boolean;
}

function TimelineItem({ activity, isLast }: TimelineItemProps) {
  const config = activityTypeConfig[activity.type?.toUpperCase()] ?? defaultConfig;
  const Icon = config.icon;

  return (
    <div className="flex gap-3">
      {/* Timeline connector */}
      <div className="flex flex-col items-center">
        <div
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${config.colorClass}`}
        >
          <Icon className="h-4 w-4" />
        </div>
        {!isLast && <div className="mt-1 w-px flex-1 bg-border" />}
      </div>

      {/* Content */}
      <div className={`min-w-0 ${isLast ? 'pb-0' : 'pb-4'}`}>
        <p className="text-sm">{activity.description}</p>
        <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
          <span>{config.label}</span>
          <span>·</span>
          <span>{formatRelativeTime(activity.date)}</span>
        </div>
      </div>
    </div>
  );
}

export function ActivityTimeline({ activities, isLoading }: ActivityTimelineProps) {
  if (isLoading) {
    return <ActivityTimelineSkeleton />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Recent Activity</CardTitle>
      </CardHeader>
      <CardContent>
        {activities.length === 0 ? (
          <div className="flex flex-col items-center py-6 text-center text-muted-foreground">
            <Activity className="mb-2 h-8 w-8 opacity-50" />
            <p className="text-sm">No recent activity</p>
          </div>
        ) : (
          <div>
            {activities.map((activity, index) => (
              <TimelineItem
                key={activity.id}
                activity={activity}
                isLast={index === activities.length - 1}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ActivityTimeline;
